import { Button } from '../ui/Button';
import { useQueueRead, useUnsentWrite } from '../ui/useConnection';
import { useFailedWriteFor } from '../../lib/failed-writes';

/**
 * Put back on one cost row. Whether a return for THIS cost is still waiting is asked of the
 * queue (`useUnsentWrite`), not of the panel's `useMutation`: that one is born idle on every
 * mount, so after a reload over a return pressed offline it would offer the press again, and
 * a second press queues a second return under a new key (Codex r2).
 *
 * And whether one was REFUSED (Inventory v2 ticket 09), the payment button's second question:
 * a return that reached the server and lost only its answer leaves `useUnsentWrite` for the
 * refused-write log, and a press here would send it again under a new key. It is retried from
 * Changes not sent, with its original key, so this stays shut until it is dealt with.
 */
export function PutBackButton({
  cost,
  listBusy,
  label,
  onPress,
}: {
  cost: { id: string; description: string };
  listBusy: boolean;
  label: string;
  onPress: () => void;
}) {
  // Both hooks on every render, then combined (TechVisitPage's rule on hook order).
  const waiting = useUnsentWrite('inventory.returnFromJob', cost.id, 'jobCostId');
  const refused = useFailedWriteFor('inventory.returnFromJob', cost.id, 'jobCostId');
  // Until the saved queue is read, "waiting" means "might be": say so, not "Waiting to send"
  // (CHECKING_LABEL's state, in words that fit a row).
  const read = useQueueRead();
  const locked = listBusy || waiting || refused;
  return (
    <Button
      variant="ghost"
      className="shrink-0"
      // The visible words lead the name, so "click Put back" finds it by voice.
      aria-label={`Put back ${cost.description}`}
      aria-disabled={locked}
      onClick={() => {
        if (!locked) onPress();
      }}
    >
      {label !== 'Put back'
        ? label
        : waiting
          ? read
            ? 'Waiting to send'
            : 'Checking…'
          : refused
            ? 'Not sent'
            : label}
    </Button>
  );
}
